import axios from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'

const API_URL = import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_URL

export type CampaignAnalytics = {
  campaignId: string
  registered: number
  claimed: number
  redeemed: number
  shared: number
}

export type AnalyticsScope = 'campaign' | 'org' | 'tenant' | 'actor' | 'journey' | 'session'

export type AnalyticsStreamRecord = {
  id: string
  fields: Record<string, string>
}

export type AnalyticsScopeSummary = Record<string, string | number | AnalyticsStreamRecord[]>
export type AnalyticsRelatedSummary = {
  id: string
  summary: AnalyticsScopeSummary
}

export type AnalyticsTalliesResponse = {
  success: boolean
  data: Record<AnalyticsScope, Record<string, AnalyticsScopeSummary>>
}

export type AnalyticsLeaderboardEntry = {
  actorType: string
  actorId: string
  /**
   * Ranking score — kept for backward compat. Equal to `bestScore` in the
   * dual-score model.
   */
  score: number
  /** Best score this player achieved in this campaign across all attempts. */
  bestScore?: number
  /** Score of the player's most recent attempt in this campaign. */
  lastScore?: number
  /**
   * Display label — captured Cognito custom:player_name when set; otherwise
   * the deterministic SHA256-anonymized "Player XXXXXX" handle. Always safe
   * to render without exposing UUIDs.
   */
  handle?: string
}

export type AnalyticsExportDataset =
  | 'events'
  | 'historical'
  | 'journeys'
  | 'sessions'
  | 'geo'
  | 'diagnostics'
  | 'portfolio'
  | 'leaderboards'
  | 'users'
  | 'quizzes'

export type AnalyticsExportJob = {
  orgId: string
  id: string
  dataset: AnalyticsExportDataset
  campaignId?: string | null
  requestedBy: string
  status: 'QUEUED' | 'RUNNING' | 'COMPLETE' | 'FAILED'
  createdAt: string
  updatedAt: string
  progressPercent?: number | null
  startedAt?: string | null
  completedAt?: string | null
  fileName?: string | null
  contentType?: string | null
  rowCount?: number | null
  errorMessage?: string | null
}

export type HistoricalAnalyticsStatus = {
  provider: 'duckdb'
  mode: 'planned' | 'remote-duckdb' | 'embedded-lake-preview'
  available: boolean
  previewAvailable: boolean
  deepQueryAvailable: boolean
  parquetStrategy: 'gated' | 'planned' | 'enabled'
  parquetBackfillEnabled: boolean
  parquetRealtimeEnabled: boolean
  queryBaseUrl?: string | null
  hasDuckDbAccess: boolean
  datasets: string[]
  storageModel: {
    raw: 'all-orgs'
    parquet: 'locked' | 'entitled-orgs'
  }
  message: string
}

export type HistoricalAnalyticsPoint = {
  date: string
  totalEvents: number
  uniqueActors: number
  uniqueJourneys: number
  uniqueSessions: number
  registrations: number
  claims: number
  redeems: number
  shares: number
  quizzesCompleted: number
  gameplays: number
  gameplaysCompleted: number
  userSessions: number
  prizesAllocated: number
  v2Wins: number
  v2Losses: number
  geoPoints: number
}

export type HistoricalAnalyticsQuery = {
  totals: {
    totalEvents: number
    uniqueActors: number
    uniqueJourneys: number
    uniqueSessions: number
    geoPoints: number
  }
  familyTotals: {
    registrations: number
    claims: number
    redeems: number
    shares: number
    quizzesCompleted: number
    gameplays: number
    gameplaysCompleted: number
    gameplayCompletionRate: number
    userSessions: number
    prizesAllocated: number
  }
  v2?: {
    familyTotals: {
      wins: number
      losses: number
      winRate: number
    }
    series: Array<{ bucket: string; wins: number; losses: number }>
  }
  durationBuckets?: Array<{ label: string; count: number }>
  series: HistoricalAnalyticsPoint[]
  topEvents: Array<{ label: string; count: number }>
  geoSources: Array<{ label: string; count: number }>
  geoCities?: Array<{ label: string; count: number }>
  quizDropOff?: Array<{ stage: number; count: number }>
  quizLeaderboard?: Array<{ actorId: string; score: number }>
  geoSeries: Array<{ date: string; geoPoints: number; gpsPoints: number; campaignLocationPoints: number }>
  recentCampaigns: Array<{ campaignId: string; count: number }>
  instrumentationCoverage: {
    totalEvents: number
    withJourneyId: number
    withSessionId: number
    withRequestId: number
    withClientIp: number
    withUserAgent: number
    termsAcceptedEvents: number
    geoTaggedEvents: number
    gpsTaggedEvents: number
    campaignLocationEvents: number
  }
  sourceSummary: {
    objectsScanned: number
    recordsParsed: number
    truncated: boolean
  }
  window: {
    days: number
    granularity?: 'minute' | 'hour' | 'day' | 'week'
    campaignId?: string | null
    previewMode: 'lake-preview'
  }
  range?: {
    from: string
    to: string
    granularity: 'minute' | 'hour' | 'day' | 'week'
  }
  dataSpan?: {
    firstEventTs: string | null
    lastEventTs: string | null
    totalObjects: number
    truncated: boolean
  }
}

export type HistoricalDeepQueryRequest = {
  campaignId?: string
  days?: number
  groupBy?: 'day' | 'week'
  metric?: 'events' | 'journeys' | 'sessions' | 'claims' | 'redeems' | 'registrations' | 'shares' | 'quizzesCompleted' | 'geoPoints'
}

export type HistoricalDeepQueryResponse = {
  success: boolean
  data?: {
    query: {
      days: number
      groupBy: 'day' | 'week'
      metric: 'events' | 'journeys' | 'sessions' | 'claims' | 'redeems' | 'registrations' | 'shares' | 'quizzesCompleted' | 'geoPoints'
      metricDescription: string
      campaignId?: string | null
      engine: 'duckdb' | 'parquet-fallback'
    }
    totals: {
      totalEvents: number
      uniqueActors: number
      uniqueJourneys: number
      uniqueSessions: number
      geoPoints: number
    }
    series: Array<{
      bucket: string
      metricValue: number
      totalEvents: number
      uniqueActors: number
      uniqueJourneys: number
      uniqueSessions: number
      registrations: number
      claims: number
      redeems: number
      shares: number
      quizzesCompleted: number
      geoPoints: number
    }>
    previousSeries: Array<{
      bucket: string
      metricValue: number
      totalEvents: number
      uniqueActors: number
      uniqueJourneys: number
      uniqueSessions: number
      registrations: number
      claims: number
      redeems: number
      shares: number
      quizzesCompleted: number
      geoPoints: number
    }>
    journeySessionSeries: Array<{
      bucket: string
      uniqueJourneys: number
      uniqueSessions: number
    }>
    topEvents: Array<{ label: string; count: number }>
    topCampaigns: Array<{ campaignId: string; count: number }>
    campaignComparisons: Array<{
      campaignId: string
      currentValue: number
      previousValue: number
      absoluteDelta: number
      percentDelta: number | null
    }>
    geoSources: Array<{ label: string; count: number }>
    geoSeries: Array<{
      bucket: string
      geoPoints: number
      gpsPoints: number
      campaignLocationPoints: number
    }>
    sourceSummary: {
      parquetFiles: number
      bytesDownloaded: number
    }
    instrumentationCoverage: {
      totalEvents: number
      withJourneyId: number
      withSessionId: number
      withRequestId: number
      withClientIp: number
      withUserAgent: number
      termsAcceptedEvents: number
      geoTaggedEvents: number
      gpsTaggedEvents: number
      campaignLocationEvents: number
    }
    periodComparison: {
      currentValue: number
      previousValue: number
      absoluteDelta: number
      percentDelta: number | null
    }
  }
  error?: string
}

export type HistoricalControlPlaneStatus = {
  storageModel: {
    raw: string
    parquet: string
  }
  parquet: {
    state: string
    enabled?: boolean
    realtimeEnabled?: boolean
    executionArn?: string | null
    startDate?: string | null
    stopDate?: string | null
    lastBackfillAt?: string | null
    lastIncrementalAt?: string | null
    lastProcessedAt?: string | null
    lastOutputKeys?: string[]
    details?: Record<string, unknown> | null
  }
  workflows: Array<{
    stateMachine: string
    status: string
    executionArn: string
    startDate?: string
    stopDate?: string
  }>
}

export type AnalyticsSimulatorRequest = {
  campaignId: string
  syntheticUsers: number
  pageViewsPerUser: number
  gameplaySessions: number
  claimRate: number
  redeemRate: number
  shareRate: number
  gpsRate: number
  intervalLabel?: string
}

export type AnalyticsSimulatorBatch = {
  campaignId: string
  intervalLabel: string
  syntheticUsers: number
  emittedEvents: number
  failedEvents: number
  gameplayAttempts: number
  gameplayStarted: number
  gameplayCompleted: number
  gameplayExhausted: number
  noActiveWindow: number
  mix: Record<string, number>
  sampleActors: string[]
  sampleGeoPoints: Array<{
    lat: number
    lon: number
    source: 'geo' | 'gps'
    actorId: string
    label: string
  }>
  notes: string[]
}

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const session = await fetchAuthSession()
  const token = session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString()
  if (!token) {
    throw new Error('You must be logged in to view analytics.')
  }
  return { Authorization: `Bearer ${token}` }
}

export const analyticsService = {
  async getCampaignAnalytics(campaignId: string): Promise<CampaignAnalytics> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: CampaignAnalytics }>(
      `${API_URL}/analytics/campaigns/${encodeURIComponent(campaignId)}`,
      { headers }
    )
    return response.data.data
  },

  async getCampaignAnalyticsBatch(campaignIds: string[]): Promise<CampaignAnalytics[]> {
    if (campaignIds.length === 0) {
      return []
    }

    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: CampaignAnalytics[] }>(
      `${API_URL}/analytics/campaigns`,
      {
        headers,
        params: {
          ids: campaignIds.join(','),
        },
      }
    )

    return response.data.data
  },

  async getTallies(params: {
    scope?: AnalyticsScope
    id?: string
    includeStream?: boolean
    streamLimit?: number
    includeRelated?: boolean
    relatedLimit?: number
  } = {}): Promise<AnalyticsTalliesResponse['data']> {
    const headers = await getAuthHeaders()
    const response = await axios.get<AnalyticsTalliesResponse>(
      `${API_URL}/analytics/tallies`,
      {
        headers,
        params: {
          scope: params.scope,
          id: params.id,
          includeStream: params.includeStream ? 'true' : undefined,
          streamLimit: typeof params.streamLimit === 'number' ? String(params.streamLimit) : undefined,
          includeRelated: params.includeRelated ? 'true' : undefined,
          relatedLimit: typeof params.relatedLimit === 'number' ? String(params.relatedLimit) : undefined,
        },
      }
    )
    return response.data.data
  },

  async getLeaderboards(params: {
    eventName: string
    campaignId?: string
    limit?: number
  }): Promise<AnalyticsLeaderboardEntry[]> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: AnalyticsLeaderboardEntry[] }>(
      `${API_URL}/analytics/leaderboards`,
      {
        headers,
        params: {
          eventName: params.eventName,
          campaignId: params.campaignId,
          limit: typeof params.limit === 'number' ? String(params.limit) : undefined,
        },
      }
    )
    return response.data.data || []
  },

  async queryOpenSearch<T = unknown>(path: string, body?: unknown, method: 'GET' | 'POST' = 'POST'): Promise<T> {
    const headers = await getAuthHeaders()
    const normalizedPath = path.replace(/^\/+/, '')
    const response = await axios.request<T>({
      url: `${API_URL}/analytics/opensearch/${normalizedPath}`,
      method,
      headers,
      data: body,
    })
    return response.data
  },

  async requestExport(dataset: AnalyticsExportDataset, campaignId?: string): Promise<AnalyticsExportJob> {
    const headers = await getAuthHeaders()
    const response = await axios.post<{ success: boolean; data: AnalyticsExportJob }>(
      `${API_URL}/analytics/exports`,
      { dataset, campaignId },
      { headers }
    )
    return response.data.data
  },

  async listExports(limit = 20): Promise<AnalyticsExportJob[]> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: AnalyticsExportJob[] }>(
      `${API_URL}/analytics/exports`,
      { headers, params: { limit } }
    )
    return response.data.data || []
  },

  async getHistoricalStatus(): Promise<HistoricalAnalyticsStatus> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: HistoricalAnalyticsStatus }>(
      `${API_URL}/analytics/historical`,
      { headers }
    )
    return response.data.data
  },

  async queryHistorical(params: {
    campaignId?: string
    days?: number
    from?: string
    to?: string
    maxObjects?: number
    granularity?: 'minute' | 'hour' | 'day' | 'week'
  } = {}): Promise<HistoricalAnalyticsQuery> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: HistoricalAnalyticsQuery }>(
      `${API_URL}/analytics/historical/query`,
      {
        headers,
        params: {
          campaignId: params.campaignId,
          days: typeof params.days === 'number' ? String(params.days) : undefined,
          from: params.from,
          to: params.to,
          maxObjects: typeof params.maxObjects === 'number' ? String(params.maxObjects) : undefined,
          granularity: params.granularity,
        },
      }
    )
    return response.data.data
  },

  async queryHistoricalDeep(payload: HistoricalDeepQueryRequest): Promise<HistoricalDeepQueryResponse> {
    const headers = await getAuthHeaders()
    const response = await axios.post<HistoricalDeepQueryResponse>(
      `${API_URL}/analytics/historical/query/deep`,
      payload,
      { headers }
    )
    return response.data
  },

  async getHistoricalControlPlaneStatus(): Promise<HistoricalControlPlaneStatus> {
    const headers = await getAuthHeaders()
    const response = await axios.get<HistoricalControlPlaneStatus>(
      `${API_URL}/analytics/historical/control-plane`,
      { headers }
    )
    return response.data
  },

  async startHistoricalParquetBackfill(payload: {
    campaignId?: string
    maxObjects?: number
    maxRecords?: number
  } = {}): Promise<{ success: boolean; executionArn?: string; state?: string }> {
    const headers = await getAuthHeaders()
    const response = await axios.post<{ success: boolean; executionArn?: string; state?: string }>(
      `${API_URL}/analytics/historical/control-plane/parquet/backfill`,
      payload,
      { headers }
    )
    return response.data
  },

  async downloadExport(jobId: string): Promise<{ blob: Blob; fileName: string }> {
    const headers = await getAuthHeaders()
    const response = await axios.get(
      `${API_URL}/analytics/exports/${encodeURIComponent(jobId)}/download`,
      { headers, responseType: 'blob' }
    )
    const contentDisposition = response.headers['content-disposition'] as string | undefined
    const matched = contentDisposition?.match(/filename="([^"]+)"/)
    return {
      blob: response.data as Blob,
      fileName: matched?.[1] || `analytics-export-${jobId}.csv`,
    }
  },

  async getCampaignDownloadEntitlement(campaignId: string): Promise<{
    campaignId: string
    purchased: boolean
    packsOwned: number
    userCapTotal: number
    claimedSessionCount: number
    canPurchase: boolean
  }> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{
      success: boolean
      data: {
        campaignId: string
        purchased: boolean
        packsOwned: number
        userCapTotal: number
        claimedSessionCount: number
        canPurchase: boolean
      }
    }>(
      `${API_URL}/analytics/campaigns/${encodeURIComponent(campaignId)}/download-entitlement`,
      { headers }
    )
    return response.data.data
  },

  async purchaseCampaignDownloadPack(params: {
    campaignId: string
    packQuantity: number
    successUrl: string
    cancelUrl: string
  }): Promise<{ url: string; sessionId: string }> {
    const headers = await getAuthHeaders()
    const response = await axios.post<{ url: string; sessionId: string }>(
      `${API_URL}/analytics/campaigns/${encodeURIComponent(params.campaignId)}/purchase-download-pack`,
      {
        packQuantity: params.packQuantity,
        successUrl: params.successUrl,
        cancelUrl: params.cancelUrl,
      },
      { headers }
    )
    return response.data
  },

  async claimCampaignDownloadPack(campaignId: string, sessionId: string): Promise<{
    campaignId: string
    claimed: boolean
    purchased: boolean
    packsOwned: number
    userCapTotal: number
  }> {
    const headers = await getAuthHeaders()
    const response = await axios.post<{
      success: boolean
      data: {
        campaignId: string
        claimed: boolean
        purchased: boolean
        packsOwned: number
        userCapTotal: number
      }
    }>(
      `${API_URL}/analytics/campaigns/${encodeURIComponent(campaignId)}/claim-download-pack`,
      { sessionId },
      { headers }
    )
    return response.data.data
  },

  async exportCampaignUsers(
    campaignId: string,
    mode: 'all' | 'latest',
    options: { includeAnswers?: boolean } = {}
  ): Promise<{ blob: Blob; fileName: string }> {
    const headers = await getAuthHeaders()
    const response = await axios.post(
      `${API_URL}/analytics/campaigns/${encodeURIComponent(campaignId)}/export-users`,
      { mode, includeAnswers: Boolean(options.includeAnswers) },
      { headers, responseType: 'blob' }
    )
    const contentDisposition = response.headers['content-disposition'] as string | undefined
    const matched = contentDisposition?.match(/filename="([^"]+)"/)
    return {
      blob: response.data as Blob,
      fileName: matched?.[1] || `campaign-${campaignId}-users.csv`,
    }
  },

  async getCampaignAnswerSummary(campaignId: string, maxQuestions = 5): Promise<{
    totalRespondents: number
    totalSessionsDetected: number
    truncated: boolean
    questions: Array<{
      questionId: string
      responseType: string
      questionOrder: number
      totalResponses: number
      topAnswers: Array<{ value: string; count: number }>
    }>
  }> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{
      success: boolean
      data: {
        totalRespondents: number
        totalSessionsDetected: number
        truncated: boolean
        questions: Array<{
          questionId: string
          responseType: string
          questionOrder: number
          totalResponses: number
          topAnswers: Array<{ value: string; count: number }>
        }>
      }
    }>(
      `${API_URL}/analytics/campaigns/${encodeURIComponent(campaignId)}/answer-summary`,
      {
        headers,
        params: { maxQuestions: String(Math.max(1, Math.min(maxQuestions, 10))) },
      }
    )
    return response.data.data
  },

  async runSimulator(payload: AnalyticsSimulatorRequest): Promise<AnalyticsSimulatorBatch> {
    const headers = await getAuthHeaders()
    const response = await axios.post<{ success: boolean; data: AnalyticsSimulatorBatch; error?: string }>(
      `${API_URL}/analytics/simulator`,
      payload,
      { headers }
    )

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Simulator batch failed.')
    }

    return response.data.data
  },
}
